import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { locationService } from '../../services/locationService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmDialog from '../Common/ConfirmDialog';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#00838F',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #006064',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const LocationList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);

  // Filter state
  const [nameFilter, setNameFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    loadLocations();
  }, []);

  // Extract unique countries when locations change
  useEffect(() => {
    const uniqueCountries = [...new Set(locations.map(loc => loc.country).filter(Boolean))];
    uniqueCountries.sort();
    setCountries(uniqueCountries);
    setFilteredLocations(locations);
  }, [locations]);

  // Apply filters when filter values or locations change
  useEffect(() => {
    let filtered = [...locations];

    if (nameFilter.trim()) {
      filtered = filtered.filter(loc =>
        loc.location_name?.toLowerCase().includes(nameFilter.toLowerCase().trim())
      );
    }

    if (countryFilter) {
      filtered = filtered.filter(loc => loc.country === countryFilter);
    }

    setFilteredLocations(filtered);
  }, [locations, nameFilter, countryFilter]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await locationService.getAll();
      setLocations(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = () => {
    navigate('/locations/new');
  };

  const handleEdit = (id) => {
    navigate(`/locations/edit/${id}`);
  };

  const handleDeleteClick = (location) => {
    setLocationToDelete(location);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!locationToDelete) return;
    try {
      await locationService.delete(locationToDelete.location_id);
      setSuccess('Location deleted successfully');
      loadLocations();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete location');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setLocationToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLocationToDelete(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!filteredLocations || filteredLocations.length === 0) return;

    const headers = ['SNo', 'Location Name', 'Country', 'Status'];

    const dataRows = filteredLocations.map((location, index) => [
      index + 1,
      location.location_name,
      location.country || '-',
      location.is_active ? 'Active' : 'Inactive',
    ]);

    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: '00838F' } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: { bottom: { style: 'medium', color: { rgb: '000000' } } },
        };
      }
    });

    dataRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'E0F7FA' } },
            alignment: { horizontal: 'left', vertical: 'center' },
          };
        }
      });
    });

    ws['!cols'] = [{ wch: 6 }, { wch: 20 }, { wch: 15 }, { wch: 10 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Locations');
    XLSX.writeFile(wb, `Locations_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!filteredLocations || filteredLocations.length === 0) return;

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor(0, 131, 143);
    doc.text('Location Management', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 28);

    const headers = [['SNo', 'Location Name', 'Country', 'Status']];

    const data = filteredLocations.map((location, index) => [
      index + 1,
      location.location_name,
      location.country || '-',
      location.is_active ? 'Active' : 'Inactive',
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 131, 143],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
      },
      alternateRowStyles: { fillColor: [224, 247, 250] },
    });

    doc.save(`Locations_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Page Header */}
      <Box sx={{
        mb: 3,
        mt: 2,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #00838F 0%, #26A69A 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(0, 131, 143, 0.3)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocationOnIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Location Management</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Manage office locations
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export to PDF">
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={exportToPDF}
                disabled={filteredLocations.length === 0}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                PDF
              </Button>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={exportToExcel}
                disabled={filteredLocations.length === 0}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Excel
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddLocation}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
              }}
            >
              Add Location
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search by Location Name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Filter by Country"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              size="small"
            >
              <MenuItem value="">All Countries</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={() => {
                setNameFilter('');
                setCountryFilter('');
              }}
              disabled={!nameFilter && !countryFilter}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: 60 }} align="center">SNo</TableCell>
              <TableCell sx={headerCellStyle}>Location Name</TableCell>
              <TableCell sx={headerCellStyle}>Country</TableCell>
              <TableCell sx={headerCellStyle}>Status</TableCell>
              <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLocations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  {locations.length === 0
                    ? 'No locations found'
                    : 'No locations match the current filters'}
                </TableCell>
              </TableRow>
            ) : (
              filteredLocations.map((location, index) => (
                <TableRow
                  key={location.location_id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#E0F7FA',
                    '&:hover': { backgroundColor: '#B2EBF2' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell><strong>{location.location_name}</strong></TableCell>
                  <TableCell>{location.country || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={location.is_active ? 'Active' : 'Inactive'}
                      color={location.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(location.location_id)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    {user?.role !== 'User' && (
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(location)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Location"
        message="Are you sure you want to delete this location?"
        details={locationToDelete ? [
          { label: 'Location', value: locationToDelete.location_name },
          { label: 'Country', value: locationToDelete.country || '-' },
        ] : []}
      />
    </Container>
  );
};

export default LocationList;
